import { QuoteFormResponseName } from '@common/constants/formResponseNames';
import type { ProspectResponse } from '@common/types/prospects';
import type { TemplateFormField } from '@common/types/templateFormFields';
import { FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import CurrencyInput from '@/components/CurrencyInput';
import { radioStyles } from './PricingInputStyles';

interface Props {
    prospect: ProspectResponse;
    quoteFormFields: TemplateFormField[];
    handleValueChange: (name: string, value: any) => any;
    readOnly: boolean;
}

export default function PurchasePricingInput({
    prospect,
    quoteFormFields,
    handleValueChange,
    readOnly,
}: Props) {
    const { prospectSummary } = prospect;
    const pricingQuestions = [
        {
            label: 'First-time home purchaser?',
            name: 'isFirstTimeHomeBuyer',
            value: prospectSummary.isFirstTimeHomeBuyer,
            isVisibleToUser:
                quoteFormFields.find(
                    (field) => field.name === QuoteFormResponseName.IS_FIRST_TIME_HOME_BUYER,
                )?.isVisibleToUser || false,
        },
        {
            label: 'Property in Toronto?',
            name: 'isPropertyInToronto',
            value: prospectSummary.isPropertyInToronto,
            isVisibleToUser:
                quoteFormFields.find(
                    (field) => field.name === QuoteFormResponseName.IS_PROPERTY_IN_TORONTO,
                )?.isVisibleToUser || false,
        },
        {
            label: 'Will there be tenants living at the property?',
            name: 'isTenantLivingInProperty',
            value: prospectSummary.isTenantLivingInProperty,
            isVisibleToUser:
                quoteFormFields.find((field) => field.name === QuoteFormResponseName.TENANTS_LIVING)
                    ?.isVisibleToUser || false,
        },
    ];

    return (
        <>
            <Grid item xs={6} lg={4}>
                <CurrencyInput
                    sx={{
                        pointerEvents: readOnly ? 'none' : undefined,
                    }}
                    label="Purchase price"
                    fullWidth
                    name={'purchasePrice'}
                    allowDecimal
                    value={prospectSummary.purchasePrice}
                    onChange={(ev) => {
                        if (!readOnly) {
                            handleValueChange('purchasePrice', ev);
                        }
                    }}
                />
            </Grid>
            {pricingQuestions.map(
                (question, index) =>
                    question.isVisibleToUser && (
                        <div key={question.label + index}>
                            <Typography variant="body1">{question.label}</Typography>
                            <FormControl>
                                <RadioGroup
                                    sx={{
                                        ...radioStyles,
                                        pointerEvents: readOnly ? 'none' : undefined,
                                    }}
                                    row
                                    aria-labelledby={question.label}
                                    name={question.name}
                                    value={question.value}
                                    onChange={(ev) => {
                                        if (!readOnly) {
                                            handleValueChange(question.name, ev.target.value);
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        value={true}
                                        control={<Radio />}
                                        label="Yes"
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        label="No"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    ),
            )}
        </>
    );
}
